<script setup lang="ts">
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'

const props = withDefaults(
  defineProps<{
    darkMode?: boolean | 'auto'
    value?: boolean
    disabled?: boolean
  }>(),
  {
    darkMode: 'auto',
    value: false,
    disabled: false,
  },
)

const emit = defineEmits<{
  (name: 'input', value: boolean): void
}>()

const onChange = (): void => {
  if (props.disabled) return
  emit('input', props.value)
}
</script>

<script lang="ts">
export default {}
</script>

<template>
  <OzBaseButton
    :class="[
      'box-border',
      'h-6',
      'w-12',
      'p-0.5',
      'rounded-2.5xl',
      'outline-none',
      // Cursor
      // When an OzToggle component is disabled, we want to show the disabled cursor when hovering both the toggle background and the indicator.
      disabled ? 'cursor-not-allowed' : 'cursor-pointer',
      // Focus ring
      'ring-offset-2',
      'focus-visible:ring-2',
      // Focus ring color
      {
        'ring-grape-500': darkMode === false,
        'ring-canary-500': darkMode === true,
        'ring-grape-500 dark:ring-canary-500': darkMode === 'auto',
      },
      // Focus ring offset color
      {
        // See `OzContainedButton`.
        'ring-offset-light-ui-100': darkMode === false,
        'ring-offset-dark-ui-100': darkMode === true,
        'ring-offset-light-ui-100 dark:ring-offset-dark-ui-100': darkMode === 'auto',
      },
      disabled &&
        value && {
          'bg-grape-500/50': darkMode === false,
          'bg-canary-500/35': darkMode === true,
          'bg-grape-500/50 dark:bg-canary-500/35': darkMode === 'auto',
        },
      disabled &&
        !value && {
          'bg-grey-200': darkMode === false,
          'bg-grey-700': darkMode === true,
          'bg-grey-200 dark:bg-grey-700': darkMode === 'auto',
        },
      !disabled &&
        value && {
          'bg-grape-500/90': darkMode === false,
          'bg-canary-500 hover-hover:hover:bg-canary-600': darkMode === true,
          'bg-grape-500/90 dark:bg-canary-500 hover-hover:dark:hover:bg-canary-600': darkMode === 'auto',
        },
      !disabled &&
        !value && {
          'bg-grey-300 hover-hover:hover:bg-grey-200': darkMode === false,
          'bg-grey-600 hover-hover:hover:bg-grey-500': darkMode === true,
          'bg-grey-300 hover-hover:hover:bg-grey-200 dark:bg-grey-600 hover-hover:dark:hover:bg-grey-500':
            darkMode === 'auto',
        },
    ]"
    :disabled="disabled"
    type="button"
    role="switch"
    :aria-checked="String(value)"
    tabindex="0"
    @click="onChange"
    @keypress.stop.space="onChange"
    @keypress.stop.enter="onChange"
  >
    <div
      :class="[
        'h-5',
        'w-5',
        'rounded-full',
        'transition-all',
        'motion-reduce:transition-none',
        'duration-250',
        value ? 'ms-6' : 'me-6',
        // Cursor
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        disabled &&
          value && {
            'bg-grey-50': darkMode === false,
            'bg-grey-900': darkMode === true,
            'bg-grey-50 dark:bg-grey-900': darkMode === 'auto',
          },
        disabled &&
          !value && {
            'bg-grey-100': darkMode === false,
            'bg-grey-800': darkMode === true,
            'bg-grey-100 dark:bg-grey-800': darkMode === 'auto',
          },
        !disabled && {
          'bg-light-ui-100': darkMode === false,
          'bg-dark-ui-100': darkMode === true,
          'bg-light-ui-100 dark:bg-dark-ui-100': darkMode === 'auto',
        },
      ]"
    ></div>
  </OzBaseButton>
</template>
